(() => {
  const SECTION_SELECTOR = '[data-music-latest-releases]';
  const IMAGE_SELECTOR = '[data-release-auto-cover]';

  const parseUrl = (value) => {
    try {
      return new URL(value);
    } catch (_error) {
      return null;
    }
  };

  const parseYouTubeId = (value) => {
    const parsed = parseUrl(value);
    if (!parsed) return null;

    const host = parsed.hostname.replace(/^www\./, '');
    const segments = parsed.pathname.split('/').filter(Boolean);

    if (host === 'youtu.be') {
      return segments[0] || null;
    }

    if (!host.endsWith('youtube.com')) return null;

    const queryId = parsed.searchParams.get('v');
    if (queryId) return queryId;

    const embedIndex = segments.findIndex((segment) => segment === 'embed');
    if (embedIndex !== -1 && segments[embedIndex + 1]) {
      return segments[embedIndex + 1];
    }

    const shortsIndex = segments.findIndex((segment) => segment === 'shorts');
    if (shortsIndex !== -1 && segments[shortsIndex + 1]) {
      return segments[shortsIndex + 1];
    }

    return null;
  };

  const parseAppleMusicId = (value) => {
    const parsed = parseUrl(value);
    if (!parsed) return null;

    const host = parsed.hostname.replace(/^www\./, '');
    if (!host.endsWith('music.apple.com')) return null;

    const trackId = parsed.searchParams.get('i');
    if (trackId && /^\d+$/.test(trackId)) return trackId;

    const segments = parsed.pathname.split('/').filter(Boolean);
    for (let index = segments.length - 1; index >= 0; index -= 1) {
      if (/^\d+$/.test(segments[index])) return segments[index];
    }

    return null;
  };

  const fetchJson = async (url) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return response.json();
    } catch (_error) {
      return null;
    }
  };

  const extractThumbnail = (data) => {
    if (!data || typeof data !== 'object') return null;

    const candidates = [
      data.thumbnail_url,
      data.thumbnailUrl,
      data.thumbnail,
      data.image,
      data.image_url,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate;
    }

    return null;
  };

  const resolveOEmbedCover = async (streamUrl, endpoints) => {
    for (const endpoint of endpoints) {
      const data = await fetchJson(endpoint);
      const thumbnail = extractThumbnail(data);
      if (thumbnail) return thumbnail;
    }

    return null;
  };

  const resolveAppleCover = async (streamUrl) => {
    const appleId = parseAppleMusicId(streamUrl);
    if (!appleId) return null;

    const data = await fetchJson(`https://itunes.apple.com/lookup?id=${encodeURIComponent(appleId)}`);
    if (!data || !Array.isArray(data.results) || data.results.length === 0) return null;

    const resultWithArtwork =
      data.results.find((item) => typeof item.artworkUrl100 === 'string') || data.results[0];

    const artworkUrl =
      resultWithArtwork.artworkUrl100 ||
      resultWithArtwork.artworkUrl60 ||
      resultWithArtwork.artworkUrl30 ||
      null;

    if (!artworkUrl || typeof artworkUrl !== 'string') return null;

    return artworkUrl.replace(/(\d+)x(\d+)bb/, '1200x1200bb');
  };

  const buildCoverCandidates = async (streamUrl) => {
    const parsed = parseUrl(streamUrl);
    if (!parsed) return [];

    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    const candidates = [];

    const youtubeId = parseYouTubeId(streamUrl);
    if (youtubeId) {
      candidates.push(`https://i.ytimg.com/vi/${youtubeId}/maxresdefault.jpg`);
      candidates.push(`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`);
    }

    if (host.endsWith('spotify.com')) {
      const spotifyCover = await resolveOEmbedCover(streamUrl, [
        `https://open.spotify.com/oembed?url=${encodeURIComponent(streamUrl)}`,
      ]);
      if (spotifyCover) candidates.push(spotifyCover);
    }

    if (host.endsWith('soundcloud.com') || host === 'on.soundcloud.com') {
      const soundcloudCover = await resolveOEmbedCover(streamUrl, [
        `https://soundcloud.com/oembed?format=json&url=${encodeURIComponent(streamUrl)}`,
      ]);
      if (soundcloudCover) candidates.push(soundcloudCover);
    }

    if (host.endsWith('mixcloud.com')) {
      const mixcloudCover = await resolveOEmbedCover(streamUrl, [
        `https://www.mixcloud.com/oembed/?format=json&url=${encodeURIComponent(streamUrl)}`,
      ]);
      if (mixcloudCover) candidates.push(mixcloudCover);
    }

    if (host.endsWith('music.apple.com')) {
      const appleCover = await resolveAppleCover(streamUrl);
      if (appleCover) candidates.push(appleCover);
    }

    return [...new Set(candidates)];
  };

  const preloadImage = (src) =>
    new Promise((resolve) => {
      const probe = new Image();
      probe.onload = () => resolve(true);
      probe.onerror = () => resolve(false);
      probe.referrerPolicy = 'no-referrer';
      probe.src = src;
    });

  const updatePlaceholder = (placeholder, message, isError = false) => {
    if (!placeholder) return;
    placeholder.textContent = message;
    placeholder.classList.toggle('is-error', isError);
  };

  const applyAutoCover = async (imageNode) => {
    if (!(imageNode instanceof HTMLImageElement)) return;
    if (imageNode.dataset.coverResolved === 'true') return;
    imageNode.dataset.coverResolved = 'true';

    const streamUrl = imageNode.getAttribute('data-stream-url') || '';
    const mediaNode = imageNode.closest('.music-release-card__media');
    const placeholder = mediaNode?.querySelector('[data-release-auto-cover-placeholder]') || null;

    if (!streamUrl.trim()) {
      updatePlaceholder(placeholder, 'Cover art unavailable', true);
      return;
    }

    const candidates = await buildCoverCandidates(streamUrl);
    for (const source of candidates) {
      const isAvailable = await preloadImage(source);
      if (!isAvailable) continue;

      imageNode.src = source;
      imageNode.removeAttribute('hidden');
      if (placeholder) placeholder.setAttribute('hidden', '');
      return;
    }

    updatePlaceholder(placeholder, 'Cover art unavailable', true);
  };

  const initSection = (section) => {
    if (!section || section.dataset.musicLatestReleasesInitialized === 'true') return;
    section.dataset.musicLatestReleasesInitialized = 'true';
    section.querySelectorAll(IMAGE_SELECTOR).forEach((imageNode) => {
      applyAutoCover(imageNode);
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      initSection(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    initAll(target);
  });
})();
